import { create } from 'zustand';
import {
  expenseCategoriesAPI,
  ExpenseCategory,
  ExpenseCategoryCreate,
  ExpenseCategoryUpdate,
} from '../services/api';

interface ExpenseCategoryState {
  categories: ExpenseCategory[];
  isLoading: boolean;
  error: string | null;

  fetchCategories: (includeInactive?: boolean) => Promise<void>;
  createCategory: (data: ExpenseCategoryCreate) => Promise<ExpenseCategory>;
  updateCategory: (id: string, data: ExpenseCategoryUpdate) => Promise<ExpenseCategory>;
  deleteCategory: (id: string) => Promise<void>;
  getCategoryById: (id: string) => ExpenseCategory | undefined;
  clearError: () => void;
}

const getErrorMessage = (error: any, defaultMsg: string): string => {
  const detail = error.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map((e: any) => `${e.loc?.slice(1).join('.')}: ${e.msg}`).join(', ') || defaultMsg;
  }
  if (typeof detail === 'string') return detail;
  return defaultMsg;
};

export const useExpenseCategoryStore = create<ExpenseCategoryState>((set, get) => ({
  categories: [],
  isLoading: false,
  error: null,

  fetchCategories: async (includeInactive = false) => {
    set({ isLoading: true, error: null });
    try {
      const categories = await expenseCategoriesAPI.list(includeInactive);
      set({ categories, isLoading: false });
    } catch (error: any) {
      set({ error: getErrorMessage(error, 'Failed to fetch expense categories'), isLoading: false });
    }
  },

  createCategory: async (data: ExpenseCategoryCreate) => {
    set({ isLoading: true, error: null });
    try {
      const category = await expenseCategoriesAPI.create(data);
      set((state) => ({ categories: [...state.categories, category], isLoading: false }));
      return category;
    } catch (error: any) {
      set({ error: getErrorMessage(error, 'Failed to create expense category'), isLoading: false });
      throw error;
    }
  },

  updateCategory: async (id: string, data: ExpenseCategoryUpdate) => {
    set({ isLoading: true, error: null });
    try {
      const category = await expenseCategoriesAPI.update(id, data);
      set((state) => ({
        categories: state.categories.map((c) => (c.id === id ? category : c)),
        isLoading: false,
      }));
      return category;
    } catch (error: any) {
      set({ error: getErrorMessage(error, 'Failed to update expense category'), isLoading: false });
      throw error;
    }
  },

  deleteCategory: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await expenseCategoriesAPI.delete(id);
      set((state) => ({
        categories: state.categories.filter((c) => c.id !== id),
        isLoading: false,
      }));
    } catch (error: any) {
      set({ error: getErrorMessage(error, 'Failed to delete expense category'), isLoading: false });
      throw error;
    }
  },

  getCategoryById: (id: string) => {
    return get().categories.find((c) => c.id === id);
  },

  clearError: () => set({ error: null }),
}));
